import * as api from "../../lib/api.ts";

/**
 * Отказы создания урока человеческими словами.
 *
 * Отдельный модуль, а не функция внутри экрана, потому что читателей у этих
 * слов несколько: окно «Новое занятие» (там урок и заводится), список уроков и
 * замена заготовки у готового урока. Текст отказа обязан быть один и тот же:
 * «HTTP 400: exercise_key_missing» в одном месте и человеческая строка в другом
 * это два разных продукта.
 *
 * ⚠️ Окно приехало сюда 17.09.2026 вместе с переводом создания урока на
 * `POST /v1/schedule/lessons`, и приехало оно именно тем отказом, о котором
 * говорит абзац выше: словарь расписания этих кодов не знал, и преподаватель
 * получал бы «HTTP 400: exercise_key_missing». Зовёт нас оттуда `errorText`
 * (`features/schedule/common.ts`), своей редакции тех же слов там нет.
 *
 * ⚠️ Тем же днём отсюда ушёл третий зачин, `editor`. Редактор заготовки завёл
 * свою форму и звал `POST /v1/lessons` напрямую, а теперь открывает то же окно,
 * и отказ приходит человеку из него. Зачин от этого не расходится: нажимали в
 * обоих случаях «Завести занятие». А вот СОВЕТ расходился бы, и поэтому он ни
 * из одной ветки никуда не отправляет: окно открывают и из редактора, где
 * человек в этой самой заготовке и стоит, и с двух экранов, где заготовка от
 * него далеко. Разбор стоит у самих кодов ниже. Недоделки, про которые редактор
 * знает сам, он и показывает сам, номерами слайдов с переходами по ним
 * (`plans/CreateLessonHere.tsx`).
 *
 * ⚠️ Отказ по недособранному заданию приходит СЮДА намеренно: компиляция такого
 * задания успешна, заготовка сохраняется, а отказ переехал на создание урока,
 * где он встречает преподавателя до занятия и его ещё можно поправить (правило 4
 * раздела 2.3 docs/choice-exercise-design.md). Но приходит он кодом, и номера
 * слайдов сервер кладёт в `details`: прочитать их обязан клиент.
 */

/**
 * Где показывается отказ. Мест два, и различаются они ЗАЧИНОМ, а это не
 * косметика.
 *
 * В окне кнопка называется «Завести занятие», и «Урок не создан» после неё
 * заставляет гадать, не завелось ли занятие без урока. Не завелось ничего, и
 * сказать надо про то, что нажимали. `cabinet` это зачин по умолчанию, и
 * задания заготовки в кабинете пересобирает одна кнопка, «Обновить из
 * заготовки» у готового урока: там речь и правда про урок.
 *
 * ⚠️ `occurrence` это окно «Новое занятие», откуда бы его ни открыли (сетка
 * расписания, список уроков, редактор заготовки), и попадают в него отказы
 * ОДНОЙ ручки, `POST /v1/schedule/lessons` (разбор в `errorText`,
 * `features/schedule/common.ts`). Заведи компиляцию ещё и «Подготовить», зачин
 * начнёт врать: занятие там уже есть, и не заводится именно урок.
 */
export type LessonErrorPlace = "cabinet" | "occurrence";

/**
 * Номера слайдов с недособранным заданием, как их назвал сервер.
 *
 * Пусто означает и «отказ не про это», и «сервер номеров не прислал». Разницы
 * для показа нет: в обоих случаях вести человека некуда.
 *
 * Тело отказа читаем мягко: это подсказка человеку, а не контракт.
 */
export function missingKeySlides(e: unknown): number[] {
  if (!(e instanceof api.ApiError) || e.code !== "exercise_key_missing") return [];
  const raw = Array.isArray(e.details.exercises) ? e.details.exercises : [];
  return [
    ...new Set(
      raw
        .map((item) =>
          typeof item === "object" && item !== null
            ? (item as { slide?: unknown }).slide
            : undefined,
        )
        .filter((slide): slide is number => typeof slide === "number"),
    ),
  ].sort((a, b) => a - b);
}

export function lessonErrorText(e: unknown, place: LessonErrorPlace = "cabinet"): string {
  if (!(e instanceof api.ApiError)) return e instanceof Error ? e.message : String(e);
  /** Что именно не получилось. Разбор у `LessonErrorPlace`. */
  const lead = place === "occurrence" ? "Занятие не заведено" : "Урок не создан";

  if (e.code === "exercise_key_missing") {
    /*
      ⚠️ **Совет не отправляет человека никуда, и это не стиль, а верность.**
      Мест, откуда читают эти слова, три, и одно из них это окно, открытое ИЗ
      редактора заготовки: там человек в той самой заготовке и стоит. «Откройте
      заготовку» ему читается как поломка. Поэтому фраза называет, что
      поправить, и молчит о том, куда идти: где искать, уже сказано первой
      половиной («в заготовке») и номерами слайдов.

      ⚠️ Номера называются словами везде, а нажать по ним нельзя нигде: переходы
      умеет только сам редактор, и свои недоделки он показывает кнопками ДО
      открытия окна (`plans/CreateLessonHere.tsx`). Сюда доезжает то, о чём его
      вкладка не знала, и без номеров заготовку пришлось бы обходить целиком.
    */
    const slides = missingKeySlides(e);
    const where =
      slides.length === 0
        ? ""
        : ` (${slides.length === 1 ? "слайд" : "слайды"} ${slides.join(", ")})`;
    // Про верный вариант тут больше не говорится: недоделка бывает и у
    // свободного ввода, и у пропусков, а вариантов там нет ни одного
    return `${lead}: в заготовке есть задание без правильного ответа${where}. Поправьте его и попробуйте ещё раз`;
  }
  if (e.code === "invalid_exercise_source") {
    // ⚠️ Клиентской проверки у этого кода нет ВОВСЕ: редактор считает только
    // недособранные ответы, а сервер отказывает ещё и на дублирующемся id
    // задания. То есть именно этот отказ доезжает до окна, открытого из
    // редактора, чаще прочих, и совет обязан быть верен для стоящего в
    // заготовке человека. Разбор целиком у соседнего кода выше
    return `${lead}: сервер не принял одно из заданий заготовки. Поправьте его и попробуйте ещё раз`;
  }
  if (e.code === "batch_too_large") {
    /*
      ⚠️ **Путь недостижим с 17.09.2026, и ветка живёт здесь до contract-фазы.**
      Пачка уроков нескольким ученикам снята вместе с уроком без даты: галочек, о
      которых говорит совет, в интерфейсе больше нет, и клиентского вызова
      `POST /v1/lessons/batch` тоже. Сама ручка жива, поэтому слова уходят ВМЕСТЕ
      с ней, одним коммитом. Снять их раньше значило бы оставить живую ручку с
      отказом без единого слова по-русски, а дописать сюда «галочек больше нет»
      значило бы объяснять человеку то, чего он не увидит.
    */
    // Потолок называем числом: без него совет «снимите лишние галочки»
    // предлагает угадывать, сколько именно
    const limit = typeof e.details.limit === "number" ? e.details.limit : null;
    const howMany = limit === null ? "" : ` Больше ${limit} за раз не создаём.`;
    return `Слишком много учеников выбрано.${howMany} Снимите лишние галочки и создайте вторую пачку`;
  }
  if (e.code === "student_not_linked") {
    return `${lead}: ученик отвязан. Обновите страницу`;
  }
  if (e.code === "exercise_kind_unsupported") {
    return `${lead}: в заготовке есть вид задания, которого сервер ещё не знает. Обновите страницу целиком`;
  }
  return e.message;
}
